import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative bg-[#0B132B] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0B132B]/95 to-[#1C2541]/70" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <BrandLogo size="md" variant="light" />
          <div className="max-w-md">
            <div className="inline-block px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              Account recovery
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Locked out? We'll send you a reset link.
            </h1>
            <p className="text-white/70 text-base mt-6 leading-relaxed">
              Enter your email and we'll send a one-time link to choose a new password.
              The link expires in 60 minutes for your security.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-[0.18em] uppercase">
            © Advantage Warranty Group
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        {submitted ? (
          <div className="w-full max-w-sm space-y-6 text-center" data-testid="forgot-sent-panel">
            <div className="lg:hidden flex justify-center"><BrandLogo size="md" /></div>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 grid place-items-center">
              <MailCheck size={30} className="text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Check your inbox</div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">Reset link sent</h2>
              <p className="text-sm text-[#6C757D] mt-4 leading-relaxed">
                If an account exists for <span className="font-semibold text-[#0B132B]">{email}</span>,
                you'll receive a reset email within a minute. Check your spam folder if you don't see it.
              </p>
            </div>
            <Link to="/login" className="inline-flex items-center text-[#0B132B] font-semibold hover:text-[#D4AF37] text-sm">
              Back to sign in <ArrowRight size={14} className="ml-1.5" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="forgot-form">
            <div className="lg:hidden"><BrandLogo size="md" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Reset password</div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">Forgot password?</h2>
              <p className="text-sm text-[#6C757D] mt-2 leading-relaxed">
                Enter the email on your account. We'll email a reset link that's valid for 60 minutes.
              </p>
            </div>
            <div>
              <Label htmlFor="fp-email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Email</Label>
              <Input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                placeholder="you@agency.com"
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
              data-testid="forgot-submit-button"
            >
              {loading ? "Sending…" : "Send reset link"}
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
            <p className="text-sm text-[#6C757D]">
              Remembered it?{" "}
              <Link to="/login" className="text-[#0B132B] font-semibold hover:text-[#D4AF37]" data-testid="forgot-back-to-login">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
